import { useCallback, useEffect, useState } from 'react';
import { MdOutlineModeEditOutline } from "react-icons/md";
import { useSelector } from 'react-redux';
import { ActionIcon, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { UsersApiResponse } from '../../../type/entities/user-type';
import { formatDistance } from 'date-fns';
import { es } from 'date-fns/locale';
import { capitalize } from 'lodash';
import moment from 'moment';
import { FaRegUserCircle } from 'react-icons/fa';
import { TbClockEdit } from 'react-icons/tb';
import { WorkHourService } from '../../../services/workHour/workHourService';
import WorkHoursFilters from './WorkHoursFilters';
import WorkHoursForm from '../components/WorkHoursForm';

const columnOrder = [
    'id',
    'company',
    'employee',
    'startTime',
    'endTime',
    'minutes',
    'workDate',
    'createdAt',
    'updatedAt',
];

interface UsersListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    workHoursSelected: any;
    setWorkHoursSelected: React.Dispatch<React.SetStateAction<any>>;
    /** Expone el refetch del listado (p. ej. import Excel sin recargar la página). */
    registerTableRefetch?: (refetch: () => void) => void;
}

const WorkHoursList: React.FC<UsersListProps> = ({
    openFormDrawer,
    setOpenFormDrawer,
    workHoursSelected,
    setWorkHoursSelected,
    registerTableRefetch,
}) => {

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);

    const service = new WorkHourService();

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE REGISTROS DE HORAS TRABAJADAS
     * @EN GETS THE LIST OF WORK HOUR REGISTERS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listWorkHours(filters);
        return response.getResponseData() as UsersApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        registerTableRefetch?.(refetch);
    }, [registerTableRefetch, refetch]);

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE UN REGISTRO DE HORA TRABAJADA
     * @EN HANDLES THE DELETION OF A WORK HOUR REGISTER
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteWorkHour(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success(response.message);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIOS REGISTROS DE HORA TRABAJADA
     * @EN HANDLES THE DELETION OF MULTIPLE WORK HOUR REGISTERS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiWorkHours(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success(response.message);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'work-hours-table'}
                    columnOrder={columnOrder}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable={true}
                    columnsNotShown={['startTime', 'endTime']}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'employee',
                            columnNameKey: 'Usuario',
                            render: (row: any) => {
                                const employee = row.row.original?.employee;
                                if (!employee) return 'N/A';
                                return (
                                    <div className="flex flex-col">
                                        <div className='flex flex-row gap-2 items-center'>
                                            <FaRegUserCircle className="text-gray-400 min-h-[15px] min-w-[15px]" />
                                            <div className="font-bold">{employee.name + ' ' + (employee?.lastName ?? '')}</div>
                                        </div>
                                        <div className="text-gray-500 text-nowrap"><span className="font-bold">Email: </span>{employee.email}</div>
                                    </div>
                                );
                            },
                        },
                        {
                            columnNameKey: 'Tiempo',
                            key: 'minutes',
                            render: (row: any) => {
                                const totalMinutes = row.row.original?.minutes;
                                const hours = Math.floor(totalMinutes / 60);
                                const minutes = totalMinutes % 60;

                                return (
                                    <span className="flex flex-nowrap gap-2 capitalize items-center">
                                        <TbClockEdit className="text-gray-400 min-h-[15px] min-w-[15px]" />
                                        <div className="font-medium text-gray-900">
                                            {hours > 0 ? `${hours}h` : ''} {minutes > 0 ? `${minutes}m` : hours === 0 ? '0m' : ''}
                                        </div>
                                    </span>
                                );
                            },
                        },
                        {
                            columnNameKey: 'Fecha del registro',
                            key: 'workDate',
                            render: (row: any) => {
                                const date = row.row.original?.workDate?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            columnNameKey: 'Actualizado',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        }
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'work_hour', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar registro'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setWorkHoursSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'work_hour', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar registro`}
                                                description={`¿Estás seguro de que deseas eliminar el registro?`}
                                                onDelete={() => handleDelete(row.id)}
                                                actionIconClassName='w-6 h-6'
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as UsersApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Registro Horario'} onClear={() => resetFilters(50)}>
                <WorkHoursFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    workHoursSelected ?
                        "Completa el formulario para editar los datos " :
                        "Completa el formulario para crear un nuevo registro "
                }
                drawerTitle={workHoursSelected ? 'Editar Registro de Horas' : 'Crear Registro de Horas'} submitText={workHoursSelected ? 'Guardar Registro' : 'Crear Registro'}>
                <WorkHoursForm setOpenModal={setOpenFormDrawer} workHourSelected={workHoursSelected} refetch={refetch} />
            </FormDrawerView>
        </Page>
    );
};

export default WorkHoursList;
